import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { createSlug } from "../utils/slug";

/**
 * Create a new prediction market
 * @param {object} marketData - Market data
 * @returns {Promise<object>} Market object with ID
 */
export const createMarket = async (marketData) => {
  try {
    const {
      title,
      category,
      creatorAddress,
      transactionHash,
      closingTime,
      yesPrice = 0.5,
      noPrice = 0.5,
    } = marketData;

    if (!title || !creatorAddress || !closingTime) {
      throw new Error("Missing required fields for market");
    }

    const market = {
      title,
      slug: createSlug(title),
      category: category || "general",
      creatorAddress: creatorAddress.toLowerCase(),
      transactionHash: transactionHash || "",
      closingTime: closingTime instanceof Date ? Timestamp.fromDate(closingTime) : closingTime,
      yesPrice: Number(yesPrice),
      noPrice: Number(noPrice),
      totalVolume: 0,
      yesPool: 0,
      noPool: 0,
      status: "active",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, "markets"), market);
    return { id: docRef.id, ...market };
  } catch (error) {
    console.error("Error creating market:", error);
    throw error;
  }
};

/**
 * Get all markets
 * @returns {Promise<Array>} Array of all markets (sorted by createdAt desc)
 */
export const getAllMarkets = async () => {
  try {
    const q = query(
      collection(db, "markets"),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting all markets:", error);
    throw error;
  }
};

/**
 * Get a specific market by ID
 * @param {string} marketId - Market document ID
 * @returns {Promise<object>} Market object
 */
export const getMarketById = async (marketId) => {
  try {
    const marketRef = doc(db, "markets", marketId);
    const marketDoc = await getDoc(marketRef);

    if (marketDoc.exists()) {
      return { id: marketDoc.id, ...marketDoc.data() };
    }

    return null;
  } catch (error) {
    console.error("Error getting market by ID:", error);
    throw error;
  }
};

/**
 * Get a specific market by slug
 * @param {string} slug - Market slug
 * @returns {Promise<object>} Market object
 */
export const getMarketBySlug = async (slug) => {
  try {
    // First try to find by slug field
    const q = query(
      collection(db, "markets"),
      where("slug", "==", slug),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }

    // If no slug found, try to find by matching title (for backwards compatibility)
    // Get all markets and find by matching slug from title
    const allMarketsQuery = query(collection(db, "markets"));
    const allMarketsSnapshot = await getDocs(allMarketsQuery);
    
    for (const doc of allMarketsSnapshot.docs) {
      const data = doc.data();
      const marketSlug = data.slug || createSlug(data.title || '');
      if (marketSlug === slug) {
        return { id: doc.id, ...data };
      }
    }

    return null;
  } catch (error) {
    console.error("Error getting market by slug:", error);
    throw error;
  }
};

/**
 * Place a trade/bet on a market
 * @param {object} tradeData - Trade data
 * @returns {Promise<object>} Trade object with ID
 */
export const placeTrade = async (tradeData) => {
  try {
    const {
      marketId,
      walletAddress,
      side,
      amount,
      price,
    } = tradeData;

    if (!marketId || !walletAddress || !side || !amount || !price) {
      throw new Error("Missing required fields for trade");
    }

    // Create trade document
    const trade = {
      marketId,
      walletAddress: walletAddress.toLowerCase(),
      side: side.toUpperCase(),
      amount: Number(amount),
      price: Number(price),
      timestamp: serverTimestamp(),
      status: "completed",
    };

    const tradeRef = await addDoc(collection(db, "trades"), trade);

    // Update market pools and prices
    const marketRef = doc(db, "markets", marketId);
    const marketDoc = await getDoc(marketRef);

    if (marketDoc.exists()) {
      const marketData = marketDoc.data();
      const newTotalVolume = (marketData.totalVolume || 0) + amount;
      const newYesPool = side.toUpperCase() === "YES" 
        ? (marketData.yesPool || 0) + amount 
        : (marketData.yesPool || 0);
      const newNoPool = side.toUpperCase() === "NO"
        ? (marketData.noPool || 0) + amount
        : (marketData.noPool || 0);

      // Recalculate prices based on pool sizes
      const totalPool = newYesPool + newNoPool;
      const newYesPrice = totalPool > 0 ? newYesPool / totalPool : 0.5;
      const newNoPrice = totalPool > 0 ? newNoPool / totalPool : 0.5;

      await updateDoc(marketRef, {
        totalVolume: newTotalVolume,
        yesPool: newYesPool,
        noPool: newNoPool,
        yesPrice: newYesPrice,
        noPrice: newNoPrice,
        updatedAt: serverTimestamp(),
      });
    }

    return { id: tradeRef.id, ...trade };
  } catch (error) {
    console.error("Error placing trade:", error);
    throw error;
  }
};

/** Max number of recent trades to fetch per market (recent activity only) */
const RECENT_TRADES_LIMIT = 100;

/**
 * Get recent trades for a market (sorted by timestamp desc)
 * @param {string} marketId - Market document ID
 * @param {number} [maxResults=RECENT_TRADES_LIMIT] - Max number of trades to return
 * @returns {Promise<Array>} Array of trades (sorted by timestamp desc, most recent first)
 */
export const getMarketTrades = async (marketId, maxResults = RECENT_TRADES_LIMIT) => {
  try {
    // Try query with index first (recent trades only)
    const q = query(
      collection(db, "trades"),
      where("marketId", "==", marketId),
      orderBy("timestamp", "desc"),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    // If index error, fallback to filtering and sorting in memory
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.warn("Index not found, using fallback query. Create the index at:", error.message?.match(/https:\/\/[^\s]+/)?.[0] || "Firebase Console");
      
      try {
        // Fetch all trades for this market without orderBy
        const q = query(
          collection(db, "trades"),
          where("marketId", "==", marketId)
        );

        const querySnapshot = await getDocs(q);
        const trades = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort in memory by timestamp (desc) and take only recent
        trades.sort((a, b) => {
          const timeA = a.timestamp?.toDate?.()?.getTime() || a.timestamp?.getTime() || 0;
          const timeB = b.timestamp?.toDate?.()?.getTime() || b.timestamp?.getTime() || 0;
          return timeB - timeA; // Descending order
        });

        return trades.slice(0, maxResults);
      } catch (fallbackError) {
        console.error("Error in fallback query:", fallbackError);
        throw fallbackError;
      }
    }
    
    console.error("Error getting market trades:", error);
    throw error;
  }
};

/**
 * Get user's positions across all markets
 * @param {string} walletAddress - Wallet address
 * @returns {Promise<Array>} Array of position objects
 */
export const getUserPositions = async (walletAddress) => {
  try {
    if (!walletAddress) return [];

    const normalizedAddress = walletAddress.toLowerCase();
    
    try {
      // Try query with index first
    const q = query(
      collection(db, "trades"),
      where("walletAddress", "==", normalizedAddress),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    } catch (error) {
      // If index error, fallback to filtering and sorting in memory
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn("Index not found, using fallback query for user positions");
        
        const q = query(
          collection(db, "trades"),
          where("walletAddress", "==", normalizedAddress)
        );

        const querySnapshot = await getDocs(q);
        const positions = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort in memory by timestamp
        positions.sort((a, b) => {
          const timeA = a.timestamp?.toDate?.()?.getTime() || a.timestamp?.getTime() || 0;
          const timeB = b.timestamp?.toDate?.()?.getTime() || b.timestamp?.getTime() || 0;
          return timeB - timeA; // Descending order
        });

        return positions;
      }
      throw error;
    }
  } catch (error) {
    console.error("Error getting user positions:", error);
    throw error;
  }
};

/**
 * Add activity to feed
 * @param {object} activityData - Activity data
 * @returns {Promise<void>}
 */
export const addActivity = async (activityData) => {
  try {
    const {
      type,
      marketId,
      walletAddress,
      action,
      side,
      amount,
    } = activityData;

    if (!type || !action) {
      throw new Error("Missing required fields for activity");
    }

    const activity = {
      type,
      marketId: marketId || "",
      walletAddress: walletAddress ? walletAddress.toLowerCase() : "",
      action,
      side: side ? side.toUpperCase() : "",
      amount: amount ? Number(amount) : 0,
      timestamp: serverTimestamp(),
    };

    await addDoc(collection(db, "activity"), activity);
  } catch (error) {
    console.error("Error adding activity:", error);
    throw error;
  }
};

/**
 * Get activity feed
 * @param {number} limitCount - Number of activities to return
 * @returns {Promise<Array>} Array of activities (sorted by timestamp desc)
 */
export const getActivityFeed = async (limitCount = 20) => {
  try {
    const q = query(
      collection(db, "activity"),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting activity feed:", error);
    throw error;
  }
};

/**
 * Create a comment on a market
 */
export const createComment = async (commentData) => {
  try {
    const { marketId, walletAddress, text } = commentData;

    if (!marketId || !walletAddress || !text || text.trim() === '') {
      throw new Error("Missing required fields for comment");
    }

    const comment = {
      marketId,
      walletAddress: walletAddress.toLowerCase(),
      text: text.trim(),
      timestamp: serverTimestamp(),
      likes: 0,
      likedBy: [],
    };

    const docRef = await addDoc(collection(db, "comments"), comment);
    return { id: docRef.id, ...comment };
  } catch (error) {
    console.error("Error creating comment:", error);
    throw error;
  }
};

/**
 * Get all comments for a market
 */
export const getMarketComments = async (marketId) => {
  try {
    const q = query(
      collection(db, "comments"),
      where("marketId", "==", marketId),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    // Fallback if index doesn't exist
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      const q = query(
        collection(db, "comments"),
        where("marketId", "==", marketId)
      );
      const querySnapshot = await getDocs(q);
      const comments = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort in memory
      comments.sort((a, b) => {
        const timeA = a.timestamp?.toDate?.()?.getTime() || a.timestamp?.getTime() || 0;
        const timeB = b.timestamp?.toDate?.()?.getTime() || b.timestamp?.getTime() || 0;
        return timeB - timeA;
      });
      return comments;
    }
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (commentId, walletAddress) => {
  try {
    const commentRef = doc(db, "comments", commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists()) {
      throw new Error("Comment not found");
    }

    const commentData = commentDoc.data();
    if (commentData.walletAddress.toLowerCase() !== walletAddress.toLowerCase()) {
      throw new Error("You can only delete your own comments");
    }

    await updateDoc(commentRef, {
      text: "[deleted]",
      deleted: true,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};

